import type { Key, ReactNode } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { useUser } from '@/allauth_auth/hooks';
import { Select } from '@/lib/base/forms/select/select';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import type { StageAssociationValue } from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import { AssociatedPipelineStageInput } from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import { useAssociationMatchers } from '@/lib/crm/hooks/useAssociationMatchers';
import { getEntityKinds } from '@/lib/crm/utils/associationMatching';
import { resolveOwnerAssociationName } from '@/lib/crm/utils/ownerAssociation';
import type { FixedValueMapping, StageMappings } from '@/lib/csv-import/api';
// ENTITY_TYPE_TO_STAGE_ASSOC is @deprecated in favour of useAssociationTypes,
// but a static lookup is right here — CSV import needs no reactive assoc state.
import { ENTITY_TYPE_TO_STAGE_ASSOC } from '@/lib/proposals/stageAssociationTypes';

import { useOrgUsers } from '../../hooks/useOrgUsers';
import { getStageLabelForSource } from './mapping-constants';
import { applyStageFallback } from './stageFallback';

const OWNER_NONE = 'none';

type Visibility = 'public' | 'private';

interface FileDefaultsPanelProps {
  orgId: number;
  entityType: string;
  /** The file_id (string form) used as `source_file_id` in fixed value mappings. */
  fileId: string;
  fixedValueMappings: FixedValueMapping[];
  onFixedValueMappingsChange: (mappings: FixedValueMapping[]) => void;
  stageMappings: StageMappings;
  onStageMappingsChange: React.Dispatch<React.SetStateAction<StageMappings>>;
}

/**
 * The single home for a file's fallbacks: what a record gets when the CSV either
 * says nothing, or says something we can't resolve.
 *
 * - **Stage** — with no stage column mapped, the value every record gets; with
 *   one mapped, what a row falls back to when its value matches nothing. This
 *   used to be TWO controls editing the same `stage_mappings[fileId].default_stage`
 *   field: one here (shown only when no column was mapped) and one inside
 *   StageValueMapping named "Default Stage for Unmatched Values" (shown only when
 *   one was). Mapping a column made the control move house and change its name.
 * - **Owner** defaults to None (unassigned) — there is no importing-user
 *   auto-assign. A matching owner column overrides it per row; a non-matching one
 *   leaves this value in place.
 * - **Visibility** (only for types with BOTH `*_visible_to_org` and
 *   `*_to_visible_user`, i.e. notes) defaults to Public. Private scopes records
 *   to the importer.
 *
 * NOTE: owner and visibility are `fixed_value_mappings`, which can only pin
 * *associations* to a user or org — despite the name it cannot set a fixed text,
 * number, or select value. Stage is a separate storage shape entirely. Unifying
 * those, and extending fallbacks to every mapped reference column (associations
 * and enums alike), is the larger piece this panel is the first step toward.
 */
export function FileDefaultsPanel({
  orgId,
  entityType,
  fileId,
  fixedValueMappings,
  onFixedValueMappingsChange,
  stageMappings,
  onStageMappingsChange,
}: FileDefaultsPanelProps) {
  const user = useUser() as { id?: number } | null;
  const currentUserId = user?.id ?? null;
  const { users } = useOrgUsers(orgId);
  const { associationTypes, entityKindMap } = useAssociationMatchers(orgId);

  const ownerAssocName = useMemo(
    () =>
      resolveOwnerAssociationName(
        associationTypes,
        entityType,
        getEntityKinds(entityType, entityKindMap)
      ),
    [associationTypes, entityType, entityKindMap]
  );
  const orgVisAssoc = `${entityType}_visible_to_org`;
  const userVisAssoc = `${entityType}_to_visible_user`;

  const hasOwner = ownerAssocName !== '';
  // A file's owner may have been saved under the legacy type-based name
  // (`${entityType}_to_owner`) before owner resolution became kind-aware, while
  // the schema now resolves the canonical kind-based name (e.g. ahoy_deal_to_owner).
  // Treat BOTH as the same logical owner slot so a saved owner is recognized on
  // read and the old row is replaced — never duplicated — on write.
  const ownerAssocNames = useMemo(() => {
    const names = new Set<string>();
    if (ownerAssocName !== '') {
      names.add(ownerAssocName);
      names.add(`${entityType}_to_owner`);
    }
    return names;
  }, [ownerAssocName, entityType]);
  const hasVisibility = useMemo(() => {
    const names = new Set(associationTypes.map(a => a.association_type_name));
    // Both sides required — a type carrying only `*_visible_to_org` as a system
    // read index (e.g. ahoy_contact_visible_to_org) has no private counterpart.
    return names.has(orgVisAssoc) && names.has(userVisAssoc);
  }, [associationTypes, orgVisAssoc, userVisAssoc]);

  const ownerRow = useMemo(() => {
    const ours = fixedValueMappings.filter(
      m =>
        String(m.source_file_id) === fileId &&
        ownerAssocNames.has(m.association_type_name)
    );
    // When both the canonical kind-based row and a legacy type-based row exist
    // for this file, prefer the canonical one so a stale legacy owner can never
    // shadow it (array order is not guaranteed). Fall back to the first legacy
    // row only when no canonical row is present.
    return (
      ours.find(m => m.association_type_name === ownerAssocName) ?? ours[0]
    );
  }, [fixedValueMappings, fileId, ownerAssocNames, ownerAssocName]);
  const visRow = useMemo(
    () =>
      fixedValueMappings.find(
        m =>
          String(m.source_file_id) === fileId &&
          (m.association_type_name === orgVisAssoc ||
            m.association_type_name === userVisAssoc)
      ),
    [fixedValueMappings, fileId, orgVisAssoc, userVisAssoc]
  );

  const ownerUserId =
    ownerRow?.target_user_id != null ? ownerRow.target_user_id : null;
  let currentVisibility: Visibility | null = null;
  if (visRow) {
    currentVisibility =
      visRow.association_type_name === userVisAssoc ? 'private' : 'public';
  }

  // Recompute this file's owner + visibility rows from the desired state and
  // replace them in one update (keeps the note "exactly one visibility" invariant).
  const applyState = useCallback(
    (nextOwnerId: number | null, nextVisibility: Visibility | null) => {
      const ours = new Set([...ownerAssocNames, orgVisAssoc, userVisAssoc]);
      const others = fixedValueMappings.filter(
        m =>
          !(
            String(m.source_file_id) === fileId &&
            ours.has(m.association_type_name)
          )
      );
      const rows: FixedValueMapping[] = [];
      if (hasOwner && nextOwnerId != null) {
        rows.push({
          source_file_id: fileId,
          association_type_name: ownerAssocName,
          target_kind: 'user',
          target_user_id: nextOwnerId,
        });
      }
      if (nextVisibility === 'public') {
        rows.push({
          source_file_id: fileId,
          association_type_name: orgVisAssoc,
          target_kind: 'org',
        });
      } else if (nextVisibility === 'private') {
        // Private = visible only to the importer (the user running the import),
        // independent of who owns the records.
        if (currentUserId != null) {
          rows.push({
            source_file_id: fileId,
            association_type_name: userVisAssoc,
            target_kind: 'user',
            target_user_id: currentUserId,
          });
        }
      }
      onFixedValueMappingsChange([...others, ...rows]);
    },
    [
      fixedValueMappings,
      fileId,
      hasOwner,
      ownerAssocName,
      ownerAssocNames,
      orgVisAssoc,
      userVisAssoc,
      currentUserId,
      onFixedValueMappingsChange,
    ]
  );

  // Seed the default Public visibility once for note-like types. Safe against
  // re-seeding: a visibility row is always present after this (toggled, never
  // removed), so the `!visRow` guard stops it firing again. Owner is NOT seeded
  // — it stays explicit (default None).
  useEffect(() => {
    if (hasVisibility && !visRow && associationTypes.length > 0) {
      applyState(ownerUserId, 'public');
    }
  }, [hasVisibility, visRow, associationTypes.length, ownerUserId, applyState]);

  const ownerItems = useMemo(() => {
    const items = [{ id: OWNER_NONE, label: 'None (unassigned)' }];
    for (const member of users) {
      const isMe = member.userId === currentUserId;
      items.push({
        id: String(member.userId),
        label: isMe ? `${member.displayName} (Me)` : member.displayName,
      });
    }
    return items;
  }, [users, currentUserId]);

  const handleOwnerChange = useCallback(
    (key: Key | null) => {
      if (key == null) {
        return;
      }
      const nextOwnerId = key === OWNER_NONE ? null : Number(key);
      applyState(nextOwnerId, currentVisibility);
    },
    [applyState, currentVisibility]
  );

  const handleVisibilityChange = useCallback(
    (key: Key | null) => {
      if (key == null) {
        return;
      }
      applyState(ownerUserId, String(key) as Visibility);
    },
    [applyState, ownerUserId]
  );

  // The stage fallback lives here in BOTH cases now: with no stage column it is
  // the value every record gets, and with a column mapped it is what a row falls
  // back to when its value matches nothing. It used to move between this panel
  // and StageValueMapping depending on that condition, editing the same
  // `default_stage` field under two different names.
  const stageLabel = getStageLabelForSource(entityType);
  const stageColumn = stageMappings[fileId]?.column ?? '';
  const hasStage = Boolean(stageLabel);

  const handleStageFallbackChange = useCallback(
    (assoc: StageAssociationValue | null) =>
      onStageMappingsChange(
        applyStageFallback(
          stageMappings,
          fileId,
          assoc ? assoc.target_entity_id : null
        )
      ),
    [fileId, stageMappings, onStageMappingsChange]
  );

  if (!hasOwner && !hasVisibility && !hasStage) {
    return null;
  }

  return (
    <section
      aria-label='Fallback values'
      className='rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] p-3'
    >
      <h4 className='mb-0.5 text-body-small font-medium text-[var(--color-text-primary)]'>
        Fallback values
      </h4>
      <p className='mb-2 text-xs text-[var(--color-text-tertiary)]'>
        We'll use these when your file doesn't give us a value, or gives one we
        can't match.
      </p>
      {/* One row per thing that can end up unresolved. Same shape as the stage
          value list below: label and scope left, control in a fixed column
          right, so every fallback in the step reads the same way. */}
      <ul className='divide-y divide-[var(--color-border-secondary)]'>
        {stageLabel && (
          <FallbackRow
            label={stageLabel}
            appliesTo={
              stageColumn
                ? `Rows whose ${stageColumn} value doesn't match`
                : 'Every imported record'
            }
          >
            <AssociatedPipelineStageInput
              value={stageMappings[fileId]?.default_stage ?? null}
              onChange={handleStageFallbackChange}
              entityType={entityType}
              associationTypeName={ENTITY_TYPE_TO_STAGE_ASSOC[entityType] ?? ''}
              ariaLabel={stageLabel}
              size='xs'
              nullable
              nullableLabel='No stage'
            />
          </FallbackRow>
        )}
        {hasOwner && (
          <FallbackRow
            label='Owner'
            appliesTo='Records with no owner from your file'
          >
            <Select
              value={ownerUserId != null ? String(ownerUserId) : OWNER_NONE}
              onChange={handleOwnerChange}
              size='xs'
              items={ownerItems}
              aria-label='Owner fallback'
            >
              {item => <SelectItem {...item} />}
            </Select>
          </FallbackRow>
        )}
        {hasVisibility && (
          <FallbackRow
            label='Visibility'
            appliesTo='Every imported record'
            tooltip='Public makes records visible to everyone in the org. Private restricts visibility to the importer and the owner.'
          >
            <Select
              value={currentVisibility ?? 'public'}
              onChange={handleVisibilityChange}
              size='xs'
              items={VISIBILITY_ITEMS}
              aria-label='Visibility fallback'
            >
              {item => <SelectItem {...item} />}
            </Select>
          </FallbackRow>
        )}
      </ul>
    </section>
  );
}

interface FallbackRowProps {
  /** Title Case, names the thing that can be unresolved. */
  label: string;
  /** Sentence case fragment: which records this value reaches. */
  appliesTo: string;
  tooltip?: string;
  children: ReactNode;
}

/**
 * One fallback in the panel. Deliberately the same left-label / right-control
 * shape as a stage value row, so the two sections read as one system rather than
 * two independently-styled panels.
 */
function FallbackRow({
  label,
  appliesTo,
  tooltip,
  children,
}: FallbackRowProps) {
  return (
    <li className='flex items-center justify-between gap-4 py-2'>
      <div className='min-w-0 flex-1'>
        <div
          className='text-sm font-medium text-[var(--color-text-primary)]'
          title={tooltip}
        >
          {label}
        </div>
        <div className='text-xs text-[var(--color-text-tertiary)]'>
          {appliesTo}
        </div>
      </div>
      <div className='w-56 shrink-0'>{children}</div>
    </li>
  );
}

const VISIBILITY_ITEMS = [
  { id: 'public', label: 'Public (everyone in org)' },
  { id: 'private', label: 'Private (importer and owner)' },
];
